import _ from 'lodash';
import moment, { Moment } from 'moment-timezone';
import { z } from 'zod';

import { UnscheduledVisitWithTentativeEdits } from '../../pages/RouteOptimizerPage/common';
import { momentFromStr } from '../../utils/datetimes/misc';
import { timeWindowSchema } from '../../utils/datetimes/types';
import { zodDatetime } from '../../utils/zodHelpers';
import { useAxleMutation, useAxleQuery } from '../apiHelpers';
import { addressWithLatLngSchema } from '../sharedSchemas/addresses';
import {
    QhpSimple,
    organizationBasicSchema,
    patientBasicSchema,
    patientSimpleSchema,
    qhpSimpleSchema,
    serviceAreaBasicSchema,
} from '../sharedSchemas/basicSchemas';
import { shiftBlockSchema, shiftSchema } from '../sharedSchemas/misc';
import { qualificationBasicSchema } from '../sharedSchemas/qualificationSchemas';
import {
    visitForListSchema,
    visitForRouteOptimizerSchema,
    visitPositionForListSchema,
} from '../sharedSchemas/visits';

const unscheduledVisitSchema = z.object({
    uuid: z.string().uuid(),
    created_at: zodDatetime(),
    timezone: z.string(),
    organization: organizationBasicSchema,
    patient: patientSimpleSchema,
    datetime: zodDatetime().nullable(),
    duration_minutes: z.number(),
    address: addressWithLatLngSchema,
    timeslot_preferences: z.array(timeWindowSchema),
    visit_positions: z.array(visitPositionForListSchema),
});

export type UnscheduledVisit = z.infer<typeof unscheduledVisitSchema>;

export const useRouteOptimizerPageData = ({
    startDate,
    endDate,
    qualification_uuids,
    service_area_uuids,
}: {
    startDate: Moment;
    endDate: Moment;
    qualification_uuids?: string[];
    service_area_uuids?: string[];
}) =>
    useAxleQuery({
        queryKey: [
            'visit-requests',
            { startDate, endDate, qualification_uuids, service_area_uuids },
        ],
        path: `/visit-requests`,
        params: {
            start_date: startDate.format('YYYY-MM-DD'),
            end_date: endDate.format('YYYY-MM-DD'),
            qualification_uuids: qualification_uuids ?? [],
            service_area_uuids: service_area_uuids ?? [],
        },
        suspense: true,
        responseSchema: z.object({
            visits_to_schedule: z.array(unscheduledVisitSchema),
            assignable_qhps: z.array(qhpSimpleSchema),
            confirmed_visits: z.array(visitForListSchema),
            assignable_shifts: z.array(shiftSchema),
            assignable_qhp_shift_blocks: z.array(shiftBlockSchema),
        }),
        transform: (data) => ({
            unscheduledVisits: data.visits_to_schedule,
            confirmedVisits: data.confirmed_visits,
            // trim first and last names to prevent items with a preceding space
            // from being sorted to the top of the list
            assignableQhps: data.assignable_qhps.map((qhp) => ({
                ...qhp,
                first_name: qhp.first_name.trim(),
                last_name: qhp.last_name.trim(),
            })),
            assignableShifts: data.assignable_shifts,
            assignableQhpShiftBlocks: data.assignable_qhp_shift_blocks,
        }),
    }).data;

export const useUnscheduledVisits = ({
    startDate,
    endDate,
    qualification_uuids,
    service_area_uuids,
}: {
    startDate: Moment;
    endDate: Moment;
    qualification_uuids?: string[];
    service_area_uuids?: string[];
}) =>
    useAxleQuery({
        queryKey: [
            'visit-requests',
            'to-schedule',
            { startDate, endDate, qualification_uuids, service_area_uuids },
        ],
        path: `/visit-requests/to-schedule`,
        params: {
            start_date: startDate.format('YYYY-MM-DD'),
            end_date: endDate.format('YYYY-MM-DD'),
            qualification_uuids: qualification_uuids ?? [],
            service_area_uuids: service_area_uuids ?? [],
        },
        suspense: true,
        responseSchema: z.object({
            visits_to_schedule: z.array(unscheduledVisitSchema),
        }),
        transform: (data) => ({
            unscheduledVisits: data.visits_to_schedule,
        }),
    }).data;

export const useConfirmedVisits = ({
    startDate,
    endDate,
    qualification_uuids,
    service_area_uuids,
}: {
    startDate: Moment;
    endDate: Moment;
    qualification_uuids?: string[];
    service_area_uuids?: string[];
}) =>
    useAxleQuery({
        queryKey: [
            'visit-requests',
            'confirmed',
            { startDate, endDate, qualification_uuids, service_area_uuids },
        ],
        path: `/visit-requests/confirmed`,
        params: {
            start_date: startDate.format('YYYY-MM-DD'),
            end_date: endDate.format('YYYY-MM-DD'),
            qualification_uuids: qualification_uuids ?? [],
            service_area_uuids: service_area_uuids ?? [],
        },
        suspense: true,
        responseSchema: z.object({
            confirmed_visits: z.array(visitForRouteOptimizerSchema),
        }),
        transform: (data) => ({
            confirmedVisits: data.confirmed_visits,
        }),
    }).data;

export const useUnscheduledVisitsCountByDate = ({
    startDate,
    endDate,
}: {
    startDate: Moment;
    endDate: Moment;
}) =>
    useAxleQuery({
        queryKey: ['visit-requests', 'count', { startDate, endDate }],
        path: `/visit-requests/count`,
        suspense: true,
        params: {
            start_date: startDate.format('YYYY-MM-DD'),
            end_date: endDate.format('YYYY-MM-DD'),
        },
        // Without this, the whole page will get suspended when the user clicks
        //  over to the next month in the day picker in the Route Optimizer page.
        keepPreviousData: true,
        placeholderData: {
            visits_by_date: {},
        },
        responseSchema: z.object({
            visits_by_date: z.record(z.number()),
        }),
    }).data.visits_by_date;

export const useFinalizeRoutes = () =>
    useAxleMutation({
        axiosConfig: (
            tentative_visits: (UnscheduledVisitWithTentativeEdits & {
                // We don't want this to be nullable here.
                tentative_start_datetime: Moment;
            })[],
        ) => ({
            method: 'post',
            path: '/visit-requests/finalize-routes',
            data: {
                tentative_visits: tentative_visits.map((tentative_visit) => ({
                    visit_uuid: tentative_visit.uuid,
                    tentative_visit_start_datetime:
                        tentative_visit.tentative_start_datetime.format(),
                    tentative_position_assignments:
                        tentative_visit.tentative_assignment !== null
                            ? _.mapValues(tentative_visit.tentative_assignment, (qhp) => qhp.uuid)
                            : null,
                })),
            },
        }),
        hardResetQueries: [['visit-requests'], ['visit-requests', 'to-schedule']],
        softResetQueries: [['visits']],
    });

export const useRouteOptimizerFilterOptions = () =>
    useAxleQuery({
        queryKey: ['scheduling-queue-filter-options'],
        path: '/visit-requests/filters',
        suspense: true,
        responseSchema: z.object({
            qualifications: z.array(qualificationBasicSchema).nullable(),
            service_areas: z.array(serviceAreaBasicSchema).nullable(),
        }),
    }).data;

const solutionSchema = z.object({
    num_unrouted_visits: z.number(),
    tentative_routed_visits: z.array(
        z.object({
            visit_uuid: z.string().uuid(),
            tentative_start_datetime: z.string().transform(momentFromStr),
            tentative_assignment: z.record(qhpSimpleSchema.nullable()),
        }),
    ),
    patients_with_overlapping_visits: z.array(patientBasicSchema),
});
export type Solution = z.infer<typeof solutionSchema>;

export const useCreateOptimizedRoutes = () =>
    useAxleMutation({
        axiosConfig: ({
            startDate,
            endDate,
            visits,
            qhps,
            useInhouseSolver,
        }: {
            startDate: Moment;
            endDate: Moment;
            visits: UnscheduledVisit[];
            qhps: QhpSimple[];
            useInhouseSolver: boolean;
        }) => ({
            method: 'post',
            path: '/visit-requests/create-optimized-routes',
            data: {
                start_datetime: moment(startDate).startOf('day').format(),
                end_datetime: moment(endDate).endOf('day').format(),
                visit_uuids: visits.map((visit) => visit.uuid),
                qhp_uuids: qhps.map((qhp) => qhp.uuid),
                use_inhouse_solver: useInhouseSolver,
            },
        }),
        responseSchema: solutionSchema,
        // Suppress default success toast. We’ll do it manually.
        defaultSuccessMessage: '',
    });
